import { Link as SiteIcon } from '@ads/icons';
import {
  AlignCenter,
  AlignJustify,
  AlignLeft,
  AlignRight,
  ArrowLeft,
  ArrowRight,
  ArrowUpCircle,
  ArrowUpRight,
  BarChartBig,
  Bold,
  Box,
  CalendarDaysIcon,
  Check,
  CheckCircle,
  CheckCircle2,
  ChevronDown,
  ChevronLeftIcon,
  ChevronRightIcon,
  CircleAlert,
  CircleAlertIcon,
  Clock,
  Copy,
  Cpu,
  Download,
  EllipsisVerticalIcon,
  ExternalLinkIcon,
  EyeOff,
  File,
  FilePlus,
  Gift,
  GitBranchPlusIcon,
  Grid2x2CheckIcon,
  Heading1,
  Heading2,
  Heading3,
  Headset,
  HomeIcon,
  Image,
  ImagePlus,
  Info,
  Italic,
  ListIcon,
  ListOrdered,
  Loader,
  Lock,
  LogOut,
  LucideProps,
  MailIcon,
  Menu,
  PanelLeft,
  PlusIcon,
  PrinterIcon,
  Puzzle,
  QrCode,
  RefreshCcw,
  RefreshCw,
  Search,
  Send,
  Settings,
  ShieldCheck,
  ShoppingBag,
  SquarePen,
  Strikethrough,
  Tag,
  ThumbsUp,
  Ticket,
  TicketPercentIcon,
  Trash2Icon,
  TriangleAlert,
  TriangleAlertIcon,
  Underline,
  Upload,
  User,
  UsersIcon,
  X,
  XCircleIcon,
  XIcon,
  Youtube,
} from 'lucide-react';
import { forwardRef } from 'react';

const iconList = {
  copy: Copy,
  'ellipsis-vertical-icon': EllipsisVerticalIcon,
  'plus-icon': PlusIcon,
  'trash-2-icon': Trash2Icon,
  ticket: Ticket,
  'users-icon': UsersIcon,
  'triangle-alert': TriangleAlert,
  loader: Loader,
  user: User,
  lock: Lock,
  'circle-alert': CircleAlert,
  'chevron-down': ChevronDown,
  menu: Menu,
  'x-icon': XIcon,
  'bar-chart-big': BarChartBig,
  'ticket-percent-icon': TicketPercentIcon,
  'external-link-icon': ExternalLinkIcon,
  'x-circle-icon': XCircleIcon,
  'check-circle-2': CheckCircle2,
  'check-circle': CheckCircle,
  check: Check,
  'eye-off': EyeOff,
  'align-justify': AlignJustify,
  'align-left': AlignLeft,
  'align-right': AlignRight,
  bold: Bold,
  heading1: Heading1,
  heading2: Heading2,
  heading3: Heading3,
  italic: Italic,
  'list-icon': ListIcon,
  'list-ordered': ListOrdered,
  'strike-through': Strikethrough,
  underline: Underline,
  'calendar-days-icon': CalendarDaysIcon,
  clock: Clock,
  'mail-icon': MailIcon,
  'file-plus': FilePlus,
  file: File,
  'square-pen': SquarePen,
  'image-plus': ImagePlus,
  'home-icon': HomeIcon,
  'chevron-right': ChevronRightIcon,
  'chevron-left': ChevronLeftIcon,
  x: X,
  'panel-left': PanelLeft,
  settings: Settings,
  'log-out': LogOut,
  'triangle-alert-icon': TriangleAlertIcon,
  'circle-alert-icon': CircleAlertIcon,
  'git-branch-plus-icon': GitBranchPlusIcon,
  download: Download,
  upload: Upload,
  refreshCCw: RefreshCcw,
  box: Box,
  grid2x2Check: Grid2x2CheckIcon,
  printer: PrinterIcon,
  headset: Headset,
  'arrow-right': ArrowRight,
  'arrow-left': ArrowLeft,
  'arrow-up-right': ArrowUpRight,
  'arrow-up-circle': ArrowUpCircle,
  'refresh-cw': RefreshCw,
  puzzle: Puzzle,
  tag: Tag,
  cpu: Cpu,
  gift: Gift,
  'thumbs-up': ThumbsUp,
  send: Send,
  shieldCheck: ShieldCheck,
  search: Search,
  info: Info,
  youtube: Youtube,
  image: Image,
  'align-center': AlignCenter,
  qrcode: QrCode,
  'shopping-bag': ShoppingBag,
  site: SiteIcon,
} as const;

export type Icons = keyof typeof iconList;

interface IconProps extends Omit<LucideProps, 'ref'> {
  icon: Icons;
}

const Icon = forwardRef<SVGSVGElement, IconProps>(({ icon, size = 16, ...props }, ref) => {
  const Icon = iconList[icon];
  return <Icon size={size} ref={ref} aria-hidden {...props} />;
});

Icon.displayName = 'Icon';

export { Icon };
